import { Box, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import {
  AttachmentGallery,
  AvatarImage,
  DEFAULT_AVATAR,
  UserHeadshotImage,
} from "@outschool/ui-components-shared";
import React from "react";

import { MessageData } from "./types";

interface MessageBubbleProps {
  message: MessageData;
  teacher: { uid: string; name?: string | null; photo?: string | null } | null;
  learnerAvatar: string | null;
  showTimestamp: boolean;
  showAvatar: boolean;
  isLastInGroup: boolean;
}

export default function MessageBubble({
  message,
  teacher,
  learnerAvatar,
  showTimestamp,
  showAvatar,
  isLastInGroup,
}: MessageBubbleProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\MessageBubble",
  );
  const isLearner = message.sender.isLearner;
  const timestamp = dayjs(message.sentAt).format("h:mm A");
  const senderName = isLearner ? t("Me") : teacher?.name ?? t("Teacher");
  const messageAttachments = message.messageContent.attachments;
  const messageVideo = message.messageContent.video;
  const hasMedia =
    (messageAttachments != null && messageAttachments.length > 0) ||
    messageVideo != null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isLearner ? "flex-end" : "flex-start",
        marginBottom: isLastInGroup ? "1.2rem" : "0.6rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isLearner ? "row-reverse" : "row",
          gap: "0.8rem",
        }}
      >
        {showAvatar ? (
          isLearner ? (
            <AvatarImage
              avatar={learnerAvatar || DEFAULT_AVATAR}
              width={32}
              height={32}
              style={{ flexShrink: 0, borderRadius: "50%" }}
            />
          ) : (
            <UserHeadshotImage
              user={teacher}
              size={32}
              sx={{ flexShrink: 0 }}
            />
          )
        ) : (
          <Box sx={{ width: 32, flexShrink: 0 }} />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isLearner ? "flex-end" : "flex-start",
            gap: "0.4rem",
            minWidth: 0,
          }}
        >
          {showTimestamp && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "0.4rem",
                alignSelf: isLearner ? "flex-end" : "flex-start",
              }}
            >
              <Typography variant="caption">{senderName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {timestamp}
              </Typography>
            </Box>
          )}
          {message.messageContent.text && (
            <Box
              sx={{
                padding: "1rem 1.4rem",
                borderRadius: "18px",
                backgroundColor: isLearner ? "primary.main" : "grey.100",
                color: isLearner ? "primary.contrastText" : "text.primary",
                width: "fit-content",
                maxWidth: "100%",
              }}
            >
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {message.messageContent.text}
              </Typography>
            </Box>
          )}
          {hasMedia && (
            <AttachmentGallery
              video={messageVideo}
              attachments={messageAttachments}
              trackingLabel="block_scoped_message_attachment"
              containerSx={{ marginBottom: 0 }}
              primaryAttachmentMaxHeight="200px"
              secondaryAttachmentMaxHeight="120px"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
